import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  CreateQuotationExceptionRequestInput,
  DecideQuotationExceptionInput,
  ListQuotationExceptionFilters,
  PaginatedResponse,
  PreviewQuotationExceptionInput,
  PreviewQuotationExceptionResult,
  QuotationExceptionCorrectionField,
  QuotationExceptionEligibleAssignee,
  QuotationExceptionRequest,
  QuotationExceptionListResponse,
  QuotationExceptionQueueSummary,
  RejectQuotationExceptionInput,
  ResubmitQuotationExceptionRequestInput,
} from '@alamo/types';
import { getApiErrorCode } from '@alamo/utils';

import { apiClient } from '../client';

import {
  type CreateManualVehicleModelPayload,
  type VehicleModel,
  vehicleCatalogKeys,
} from './admin/useVehicleCatalog';

export const quotationExceptionKeys = {
  all: ['quotation-exception-requests'] as const,
  list: (filters?: ListQuotationExceptionFilters, admin = false) =>
    ['quotation-exception-requests', admin ? 'admin' : 'own', filters ?? {}] as const,
  detail: (id: string, admin = false) =>
    ['quotation-exception-requests', admin ? 'admin' : 'own', id] as const,
  vehicleCategories: (scope: 'all' | 'manual_model') =>
    ['quotation-exception-requests', 'vehicle-categories', scope] as const,
  eligibleAssignees: (filters: { search?: string; page?: number; limit?: number }) =>
    ['quotation-exception-requests', 'eligible-assignees', filters] as const,
};

export function useQuotationExceptionEligibleAssignees(
  filters: { search?: string; page?: number; limit?: number } = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: quotationExceptionKeys.eligibleAssignees(filters),
    queryFn: async () => {
      const response = await apiClient.get<
        ApiSuccess<{ count: number; rows: QuotationExceptionEligibleAssignee[] }>
      >('/admin/quotation-exception-requests/eligible-assignees', { params: filters });
      return response.data.data;
    },
    enabled: options.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useQuotationExceptionVehicleCategories(options: { manualOnly?: boolean } = {}) {
  const scope = options.manualOnly ? 'manual_model' : 'all';
  return useQuery({
    queryKey: quotationExceptionKeys.vehicleCategories(scope),
    queryFn: async () => {
      const response = await apiClient.get<
        ApiSuccess<Array<{ id: string; code: string; name: string }>>
      >('/quotation-exception-requests/vehicle-categories', { params: { scope } });
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateQuotationException() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      quotationId,
      input,
    }: {
      quotationId: string;
      input: CreateQuotationExceptionRequestInput;
    }) => {
      const response = await apiClient.post<ApiSuccess<QuotationExceptionRequest>>(
        `/quotations/${quotationId}/exception-requests`,
        input,
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
      );
      return response.data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: quotationExceptionKeys.all }),
  });
}

export function useQuotationExceptions(
  filters?: ListQuotationExceptionFilters,
  options: { admin?: boolean; enabled?: boolean; refetchInterval?: number } = {},
) {
  const admin = options.admin ?? false;
  return useQuery({
    queryKey: quotationExceptionKeys.list(filters, admin),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<QuotationExceptionListResponse>>(
        admin ? '/admin/quotation-exception-requests' : '/quotation-exception-requests',
        { params: filters },
      );
      const raw = response.data.data;
      const page = filters?.page ?? 1;
      const limit = filters?.limit ?? 20;
      const emptySummary: QuotationExceptionQueueSummary = {
        awaitingAssignment: 0,
        inReview: 0,
        awaitingRequester: 0,
        overdue: 0,
        completedToday: 0,
        mine: 0,
        approved: 0,
        partiallyApproved: 0,
        rejected: 0,
        pending: 0,
        total: 0,
      };
      return {
        items: raw.rows,
        total: raw.count,
        page,
        limit,
        totalPages: Math.ceil(raw.count / limit),
        summary: raw.summary ?? emptySummary,
      } satisfies PaginatedResponse<QuotationExceptionRequest> & {
        summary: QuotationExceptionQueueSummary;
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 20_000,
    refetchInterval: options.refetchInterval,
  });
}

export function useQuotationExceptionDetail(
  id: string | undefined,
  options: { admin?: boolean } = {},
) {
  const admin = options.admin ?? false;
  return useQuery({
    queryKey: quotationExceptionKeys.detail(id ?? '', admin),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<QuotationExceptionRequest>>(
        `${admin ? '/admin' : ''}/quotation-exception-requests/${id}`,
      );
      return response.data.data;
    },
    enabled: Boolean(id),
  });
}

function useExceptionAction<TInput, TResult = QuotationExceptionRequest>(id: string, path: string) {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (input: TInput) => {
      const response = await apiClient.post<ApiSuccess<TResult>>(
        `/admin/quotation-exception-requests/${id}/${path}`,
        input,
      );
      return response.data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: quotationExceptionKeys.all }),
    onError: async (error) => {
      // A concurrent queue action can change both assignment and available controls.
      if (
        [
          'QUOTATION_EXCEPTION_ASSIGNMENT_CONFLICT',
          'QUOTATION_EXCEPTION_INVALID_STATUS',
          'QUOTATION_EXCEPTION_NOT_ASSIGNEE',
        ].includes(getApiErrorCode(error) ?? '')
      ) {
        await queryClient.invalidateQueries({ queryKey: quotationExceptionKeys.all });
      }
    },
  });
}

export function useClaimQuotationException(id: string) {
  return useExceptionAction<Record<string, never>>(id, 'claim');
}

export function useReleaseQuotationException(id: string) {
  return useExceptionAction<{ reason: string }>(id, 'release');
}

export function useAssignQuotationException(id: string) {
  return useExceptionAction<{ assigneeUserId?: string | null; reason: string }>(id, 'assign');
}

export function useRequestQuotationExceptionChanges(id: string) {
  return useExceptionAction<{
    publicReason: string;
    requiredFields: QuotationExceptionCorrectionField[];
  }>(id, 'request-changes');
}

export function usePreviewQuotationException(id: string) {
  return useExceptionAction<PreviewQuotationExceptionInput, PreviewQuotationExceptionResult>(
    id,
    'preview',
  );
}

export function useDecideQuotationException(id: string) {
  return useExceptionAction<DecideQuotationExceptionInput>(id, 'decision');
}

export function useRejectQuotationException(id: string) {
  return useExceptionAction<RejectQuotationExceptionInput>(id, 'reject');
}

export function useCreateQuotationExceptionManualModel(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateManualVehicleModelPayload) => {
      const response = await apiClient.post<ApiSuccess<VehicleModel>>(
        `/admin/quotation-exception-requests/${id}/models/manual`,
        input,
      );
      return response.data.data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: quotationExceptionKeys.all }),
        queryClient.invalidateQueries({ queryKey: vehicleCatalogKeys.all }),
      ]);
    },
  });
}

export function useResubmitQuotationException(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ResubmitQuotationExceptionRequestInput) => {
      const response = await apiClient.post<ApiSuccess<QuotationExceptionRequest>>(
        `/quotation-exception-requests/${id}/resubmit`,
        input,
      );
      return response.data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: quotationExceptionKeys.all }),
  });
}

export function useCancelQuotationException(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (reason: string) => {
      const response = await apiClient.post<ApiSuccess<QuotationExceptionRequest>>(
        `/quotation-exception-requests/${id}/cancel`,
        { reason },
      );
      return response.data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: quotationExceptionKeys.all }),
  });
}
